import { type FC, type PropsWithChildren, type ReactNode } from 'react';

import { useAtomValue } from 'jotai';

import { useCameraSetup, useSwing, useSwingAnalysis, useSwingVideos } from '@queries';
import { ErrorScreen, LoadingScreen } from '@screens';
import {
    activeAnalysisAtom,
    activeSwingAtom,
    activeSwingIdAtom,
    activeUserSettingsAtom,
    activeVideoAtom,
    comparisonAnalysisAtom,
    comparisonCameraSetupAtom,
    comparisonSwingAtom,
    comparisonVideoAtom,
    isCurrentActivityAtom,
    useSyncAtom,
    withSwingScope,
} from '@store';

const SwingGuard: FC<PropsWithChildren<{ errAction: ReactNode }>> = ({ children, errAction }) => {
    const activeSwingId = useAtomValue(activeSwingIdAtom);
    const isCurrentActivity = useAtomValue(isCurrentActivityAtom);
    const { comparisonSwingId } = useAtomValue(activeUserSettingsAtom);

    const activeSwingResponse = useSwing(activeSwingId);
    useSyncAtom(activeSwingAtom, activeSwingResponse.data);

    const activeSwingAnalysisResponse = useSwingAnalysis(activeSwingId);
    useSyncAtom(activeAnalysisAtom, activeSwingAnalysisResponse.data);
    const activeAnalysis = useAtomValue(activeAnalysisAtom);
    const activeSwingVideoResponse = useSwingVideos(activeSwingId);
    useSyncAtom(activeVideoAtom, activeSwingVideoResponse.data);

    const comparisonSwingResponse = useSwing(comparisonSwingId ?? '');
    useSyncAtom(comparisonSwingAtom, comparisonSwingResponse.data, true);

    const comparisonSwingAnalysisResponse = useSwingAnalysis(comparisonSwingId ?? '');
    useSyncAtom(comparisonAnalysisAtom, comparisonSwingAnalysisResponse.data, true);

    const comparisonCameraSetupRequest = useCameraSetup(comparisonSwingResponse.data?.activityId ?? -1);
    useSyncAtom(comparisonCameraSetupAtom, comparisonCameraSetupRequest?.data);

    const comparisonSwingVideoResponse = useSwingVideos(comparisonSwingId ?? '');
    useSyncAtom(comparisonVideoAtom, comparisonSwingVideoResponse.data, true);

    const shouldWaitForAnalysis = !isCurrentActivity && !activeAnalysis;

    if (activeSwingAnalysisResponse.isLoading || activeSwingVideoResponse.isLoading || shouldWaitForAnalysis)
        return <LoadingScreen message="Loading swing analysis data..." />;
    if (activeSwingAnalysisResponse.isError || activeSwingVideoResponse.isError)
        return <ErrorScreen message="Error loading swing analysis." action={errAction} />;

    return children;
};

export const SwingLoader = withSwingScope(SwingGuard);
